/**
 * Super admin: export one or more people (with all their medicines, library,
 * reminders, visits, doctors, tests and vaccines) and import them into any family.
 *
 * File format "bhorosha-people-v1". Import also accepts a full backup from
 * the old Medicine Purchase Calculator ("mpc-multi-person-v1").
 */
import { genId } from '@bhorosha/core';
import { BhStore, PEOPLE_KEY, type Backend, type DataRow, type PatientTable } from '@bhorosha/data';

const COLLECTIONS: { key: string; table: PatientTable; prefix: string }[] = [
  { key: 'medicines', table: 'medicines', prefix: 'mpc_medicines_v1' },
  { key: 'library', table: 'library_items', prefix: 'mpc_library_v1' },
  { key: 'reminders', table: 'reminders', prefix: 'mpc_reminders_v1' },
  { key: 'visits', table: 'visits', prefix: 'mpc_visits_v1' },
  { key: 'doctors', table: 'doctors', prefix: 'mpc_doctors_v1' },
  { key: 'tests', table: 'tests', prefix: 'mpc_tests_v1' },
  { key: 'vaccines', table: 'vaccinations', prefix: 'mpc_vaccines_v1' },
];

type Person = { id: string; name?: string; [k: string]: unknown };
export interface PersonBundle {
  person: Person;
  medicines: unknown[]; library: unknown[]; reminders: unknown[]; visits: unknown[]; doctors: unknown[]; tests: unknown[]; vaccines: unknown[];
}
export interface PeopleFile {
  format: 'bhorosha-people-v1';
  exported_at: string;
  family_name?: string;
  people: PersonBundle[];
  medicine_types?: unknown[];
  test_library?: unknown[];
}

const byPos = (rows: DataRow[]) => rows.slice().sort((a, b) => a.position - b.position).map(r => r.data);

/** Build the export file for the chosen people of a family. */
export async function exportPeople(backend: Backend, familyId: string, familyName: string, personIds: string[]): Promise<PeopleFile> {
  const snap = await backend.loadFamilyData(familyId);
  const people = byPos(snap.rows.filter(r => r.table === 'patients')) as Person[];
  const list = people.filter(p => personIds.includes(p.id));
  return {
    format: 'bhorosha-people-v1',
    exported_at: new Date().toISOString(),
    family_name: familyName,
    people: list.map(person => {
      const b: Record<string, unknown> = { person };
      for (const c of COLLECTIONS) b[c.key] = byPos(snap.rows.filter(r => r.table === c.table && r.patientId === person.id));
      return b as unknown as PersonBundle;
    }),
    medicine_types: byPos(snap.rows.filter(r => r.table === 'medicine_types')),
    test_library: byPos(snap.rows.filter(r => r.table === 'test_library')),
  };
}

export function summarize(b: PersonBundle) {
  return `${b.medicines.length} medicines, ${b.reminders.length} reminders, ${b.visits.length} visits, ${b.tests.length} tests${b.vaccines?.length ? `, ${b.vaccines.length} vaccines` : ''}`;
}

/** Read an export file (or an old full backup). Returns null if it isn't one. */
export function parsePeopleFile(text: string): PeopleFile | null {
  let d: any;
  try { d = JSON.parse(String(text).replace(/^﻿/, '').trim()); } catch { return null; }
  if (!d || typeof d !== 'object') return null;
  const arr = (v: unknown) => (Array.isArray(v) ? v : []);
  if (d.format === 'bhorosha-people-v1' && Array.isArray(d.people)) {
    return { ...d, people: d.people.filter((b: any) => b && b.person && b.person.id).map((b: any) => ({
      person: b.person, ...Object.fromEntries(COLLECTIONS.map(c => [c.key, arr(b[c.key])])),
    })) };
  }
  // Old app: full backup (people + data), or all_storage only.
  let people: Person[] = arr(d.people) as Person[];
  const fromStorage = (prefix: string, pid: string) => {
    try { return arr(JSON.parse(d.all_storage?.[`${prefix}::${pid}`] || '[]')); } catch { return []; }
  };
  if (!people.length && d.all_storage?.mpc_people_v1) { try { people = JSON.parse(d.all_storage.mpc_people_v1); } catch { /* ignore */ } }
  if (!people.length) return null;
  const entries = arr(d.data) as any[];
  return {
    format: 'bhorosha-people-v1',
    exported_at: d.exported_at || new Date().toISOString(),
    people: people.filter(p => p && p.id).map(person => {
      const e = entries.find(x => x.person_id === person.id) || {};
      return { person, ...Object.fromEntries(COLLECTIONS.map(c => [c.key, Array.isArray(e[c.key]) ? e[c.key] : fromStorage(c.prefix, person.id)])) } as unknown as PersonBundle;
    }),
    medicine_types: arr(d.medicine_types),
    test_library: arr(d.test_library),
  };
}

class MemStorage {
  private m = new Map<string, string>();
  getItem(k: string) { return this.m.has(k) ? this.m.get(k)! : null; }
  setItem(k: string, v: string) { this.m.set(k, v); }
  removeItem(k: string) { this.m.delete(k); }
}

export type ImportMode = { kind: 'new' } | { kind: 'replace'; targetId: string };

/**
 * Import people into a family. "new" adds each as a new person; "replace"
 * (one person only) overwrites an existing person's details and records.
 * Medicine types used by the imported medicines are added if missing.
 */
export async function importPeople(backend: Backend, familyId: string, userId: string, file: PeopleFile, chosen: number[], mode: ImportMode) {
  const store = new BhStore({ familyId, userId, role: 'owner', backend, persist: new MemStorage(), debounceMs: 0 });
  await store.refresh();
  const people = JSON.parse(store.getItem(PEOPLE_KEY) || '[]') as Person[];
  const added: string[] = [];

  for (const i of chosen) {
    const b = file.people[i];
    if (!b) continue;
    let id: string;
    if (mode.kind === 'replace') {
      id = mode.targetId;
      const idx = people.findIndex(p => p.id === id);
      const next = { ...b.person, id };
      if (idx >= 0) people[idx] = next; else people.push(next);
    } else {
      id = people.some(p => p.id === b.person.id) ? genId() : b.person.id;
      people.push({ ...b.person, id });
    }
    added.push(String(b.person.name || 'Unnamed'));
    store.setItem(PEOPLE_KEY, JSON.stringify(people));
    for (const c of COLLECTIONS) store.setItem(`${c.prefix}::${id}`, JSON.stringify((b as unknown as Record<string, unknown[]>)[c.key] || []));
  }

  // Add any medicine types / tests the imported data uses but this family lacks.
  const mergeList = (key: string, incoming: unknown[] | undefined) => {
    if (!incoming || !incoming.length) return;
    const have = JSON.parse(store.getItem(key) || '[]') as { id?: string }[];
    const ids = new Set(have.map(x => x.id));
    const extra = (incoming as { id?: string }[]).filter(x => x && x.id && !ids.has(x.id));
    if (extra.length || !have.length) store.setItem(key, JSON.stringify(have.length ? [...have, ...extra] : incoming));
  };
  mergeList('mpc_types_v1', file.medicine_types);
  mergeList('mpc_testlib_v1', file.test_library);

  for (let i = 0; i < 5 && store.pending; i++) await store.flush();
  if (store.pending) throw new Error('Some of the data could not be saved. Check your connection and try again.');
  return added;
}

export function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
